import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  SIDEBAR_OVERLAY_BREAKPOINT,
  setSidebarMobileOpen,
  toggleSidebar,
} from "../../store/slices/uiSlice";
import HelpDropdownMenu from "../HelpDropdownMenu";
import NotificationCenterIcon from "../NotificationCenterIcon";
import UpgradeBanner from "../UpgradeBanner";
import UserDropdownMenu from "../UserDropdownMenu";
import { NAV_ITEMS, RAIL_ROW, RAIL_ROW_IDLE } from "./navItems";

/**
 * One curve for everything the rail animates — its own width, the panel that
 * floats over the page during a peek, and the brand mark that grows with it —
 * so the whole rail moves as a single object.
 */
const RAIL_TRANSITION = "duration-300 ease-in-out";

/** Rail widths. Kept here so the main column can offset by the same values. */
export const SIDEBAR_WIDTH_EXPANDED = "w-52";
export const SIDEBAR_WIDTH_COLLAPSED = "w-14";

/**
 * The app's primary navigation: a vertical rail that collapses to icons only.
 *
 * Collapsed, the rail expands again while the pointer is over the nav rows so
 * labels are one hover away. That peek floats above the page rather than
 * widening the column, so content never reflows under the mouse. The brand
 * band at the top neither opens nor closes the peek: its own controls move
 * with the rail's width, so hovering there leaves that width as it found it.
 *
 * Below `lg` it becomes an off-canvas overlay (always at full width, since an
 * icon-only overlay would be pointless) driven by `sidebarMobileOpen`.
 */
export default function AppSidebar() {
  const dispatch = useAppDispatch();
  const collapsed = useAppSelector((s) => s.uiReducer.sidebarCollapsed);
  const mobileOpen = useAppSelector((s) => s.uiReducer.sidebarMobileOpen);
  const location = useLocation();
  const [hovered, setHovered] = useState(false);
  const [canHover, setCanHover] = useState(true);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  // Navigating is the point of the overlay, so every navigation dismisses it.
  useEffect(() => {
    dispatch(setSidebarMobileOpen(false));
  }, [location.pathname, dispatch]);

  // Growing past the breakpoint turns the overlay back into a column; drop the
  // overlay flag so it cannot force the rail wide while it is set to collapse.
  useEffect(() => {
    const query = window.matchMedia(
      `(min-width: ${SIDEBAR_OVERLAY_BREAKPOINT}px)`,
    );
    const sync = () => {
      if (query.matches) dispatch(setSidebarMobileOpen(false));
    };
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [dispatch]);

  // The peek needs a pointer that can hover. On a touch screen the same
  // gesture would latch the rail open on the first tap and shift the link out
  // from under the finger that was aiming at it.
  useEffect(() => {
    const query = window.matchMedia("(hover: none)");
    const sync = () => setCanHover(!query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  // Escape closes the overlay, the usual contract for a modal surface.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setSidebarMobileOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, dispatch]);

  // The overlay is never icon-only: there is no space pressure there.
  const railCollapsed = collapsed && !mobileOpen;

  // A menu opened from the rail portals out of the aside, so the pointer
  // leaving would otherwise collapse the rail out from under the panel the
  // user just opened. Hold the peek until every menu is closed again.
  const anyMenuOpen = Object.values(openMenus).some(Boolean);
  const menuHandlers = useMemo(
    () => ({
      notifications: (open: boolean) =>
        setOpenMenus((prev) => ({ ...prev, notifications: open })),
      help: (open: boolean) =>
        setOpenMenus((prev) => ({ ...prev, help: open })),
      user: (open: boolean) =>
        setOpenMenus((prev) => ({ ...prev, user: open })),
    }),
    [],
  );

  const peeking = railCollapsed && canHover && (hovered || anyMenuOpen);
  const iconOnly = railCollapsed && !peeking;

  return (
    <>
      {mobileOpen && (
        <div
          role="presentation"
          onClick={() => dispatch(setSidebarMobileOpen(false))}
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
        />
      )}

      {/* The off-canvas slide is a transform, and a transform is never `none`
          here — so the aside is its own stacking context on every breakpoint,
          including the desktop column. That makes the rail's z-index the only
          thing standing between the peek panel and the page: everything inside
          the aside is trapped below it. Left at `auto` the rail would paint at
          layer 0, under every positioned element in the content column — the
          dashboard panels, the site cards, the device search box — so it stays
          explicitly above them at all widths. */}
      <aside
        onPointerLeave={() => setHovered(false)}
        className={`fixed inset-y-0 left-0 z-50 shrink-0 transition-[width,transform] ${RAIL_TRANSITION} lg:relative lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } ${railCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED}`}
      >
        {/* The panel floats inside the aside and is the only thing that ever
            changes width, so opening, closing and peeking are all the same
            tween. The aside behind it just reserves the column's share of the
            layout: it matches the panel when the rail is pinned open, and
            stays narrow during a peek so the page never reflows under the
            mouse. Keeping the panel absolute in both cases matters —
            `position` cannot be interpolated, so switching it would snap. */}
        <div
          className={`absolute inset-y-0 left-0 z-50 flex flex-col overflow-hidden border-r border-neutral-800 bg-neutral-850 transition-[width,box-shadow] ${RAIL_TRANSITION} ${
            iconOnly ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED
          } ${peeking ? "shadow-xl shadow-black/50" : ""}`}
        >
          {/* Brand: the mark alone, sized to whatever width the rail has.
              The band keeps one height in both states — the collapse toggle
              moves in and out of the flow, and letting that change the height
              would shunt every nav row up and down mid-transition.

              It is also the one part of the rail that never starts a peek.
              Everything in this band moves when the rail widens — the mark
              grows, the toggle leaves the flow for the right edge — so
              opening on approach would pull both out from under the pointer
              aiming at them. It does not end one either: a rail already open
              is already still, and collapsing it here would move those same
              two controls for the same bad reason. The band leaves the width
              exactly as it found it. */}
          <div
            className={`relative flex h-[4.5rem] shrink-0 items-center justify-center border-b border-neutral-800 ${
              iconOnly ? "flex-col gap-1.5" : "px-3"
            }`}
          >
            <Link
              to="/home"
              aria-label="Insaion home"
              className="flex items-center justify-center"
            >
              <img
                src="/InsaionIcon.png"
                alt="Insaion"
                className={`transition-[height,width] ${RAIL_TRANSITION} ${
                  iconOnly ? "h-8 w-8" : "h-14 w-14"
                }`}
              />
            </Link>

            <button
              type="button"
              onClick={() => dispatch(toggleSidebar())}
              title={railCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={railCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!railCollapsed}
              className={`hidden rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-200 lg:block ${
                iconOnly ? "" : "absolute right-2"
              }`}
            >
              {railCollapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>

            {/* Overlay-only dismissal; the collapse toggle is meaningless there. */}
            <button
              type="button"
              onClick={() => dispatch(setSidebarMobileOpen(false))}
              aria-label="Close navigation"
              className="absolute right-2 rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-200 lg:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* The upgrade path rides directly under the mark, in the band the
              eye lands on first. It renders nothing at all on a paid plan, and
              on a free one it is a rail row of the same height in both states,
              so the nav below it starts at the same offset either way. */}
          <UpgradeBanner
            collapsed={iconOnly}
            onPointerEnter={() => setHovered(true)}
          />

          <nav
            onPointerEnter={() => setHovered(true)}
            aria-label="Main navigation"
            className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3"
          >
            <ul className="flex flex-col gap-0.5">
              {NAV_ITEMS.map(({ to, label, icon: Icon, id }) => (
                <li key={to}>
                  <NavLink
                    id={id}
                    to={to}
                    title={iconOnly ? label : undefined}
                    className={({ isActive }) =>
                      `${RAIL_ROW} ${
                        isActive
                          ? "bg-neutral-750/70 text-neutral-50 after:absolute after:left-0 after:top-1.5 after:bottom-1.5 after:w-0.5 after:rounded-full after:bg-orange-500"
                          : RAIL_ROW_IDLE
                      }`
                    }
                  >
                    <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span className={iconOnly ? "sr-only" : "truncate"}>
                      {label}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div
            onPointerEnter={() => setHovered(true)}
            className="shrink-0 border-t border-neutral-800 px-2 py-3"
          >
            {/* Notifications and help are destinations of their own, so they
                sit in the list rather than as loose icons beside the avatar. */}
            <ul className="mb-2 flex flex-col gap-0.5">
              <li>
                <NotificationCenterIcon
                  collapsed={iconOnly}
                  onOpenChange={menuHandlers.notifications}
                />
              </li>
              <li>
                <HelpDropdownMenu
                  collapsed={iconOnly}
                  onOpenChange={menuHandlers.help}
                />
              </li>
            </ul>

            {/* No centring wrapper: `iconOnly` flips the instant the toggle
                is pressed, while the panel takes the full tween to reach its
                new width. Centring the avatar would snap it to the middle of
                a panel that is still 208px wide and then walk it left as the
                panel closed. Like every other row, it keeps one offset from
                the left edge and simply loses its label. */}
            <UserDropdownMenu
              collapsed={iconOnly}
              className="w-full"
              onOpenChange={menuHandlers.user}
            />
          </div>
        </div>
      </aside>
    </>
  );
}
